import _ from 'lodash-es'

import { sortEnumValues } from '../../../../../helpers/utils'
import { DashboardChartExports } from '../dashboard-state-service'

export interface SunburstData {
  itemStyle: { color: string }
  name: string
  value?: number
  children?: SunburstData[]
}

// The Figma-defined categorical chart palette (12 hues), exposed as CSS custom
// properties by verity-ui-common. ECharts renders to <canvas>, so it needs
// literal color values, not var(...) strings -- this reads the currently
// resolved value of each custom property so charts reflect the active theme
// instead of hardcoding one mode's colors.
const CHART_PALETTE_TOKENS = [
  '--chart-red',
  '--chart-pink',
  '--chart-magenta',
  '--chart-orange',
  '--chart-yellow',
  '--chart-lime',
  '--chart-green',
  '--chart-teal',
  '--chart-cyan',
  '--chart-blue',
  '--chart-violet',
  '--chart-purple',
]

export function getChartPalette(repeat = 1): string[] {
  const styles = getComputedStyle(document.documentElement)
  const palette = CHART_PALETTE_TOKENS.map((token) =>
    styles.getPropertyValue(token).trim(),
  )
  return Array.from({ length: repeat }, () => palette).flat()
}

export class ChartUtils {
  // Cycled to the same length as the original hardcoded array (30) so any
  // existing index-based lookup (this.chartColors[index]) keeps working.
  public static get sunburstColors(): string[] {
    return getChartPalette(3)
  }
}

export function findLongestHeatmapColumn(arrays): number {
  // Create an object to keep track of occurrences of each number in the 0th index
  const countMap = {}

  // Iterate through each subarray
  for (const subarray of arrays) {
    if (subarray.length > 0) {
      // Ensure subarray is not empty
      const key = subarray[0] // Get the number in the 0th index
      // Increment the count for this number
      countMap[key] = (countMap[key] || 0) + 1
    }
  }

  // Find the maximum occurrence count
  let maxCount = 0
  for (const key in countMap) {
    if (countMap[key] > maxCount) {
      maxCount = countMap[key]
    }
  }

  // Return the maximum count
  return maxCount
}

/**
 * Hides unselected report selections and add page breaks
 *
 * @param exportOptions - An array of strings representing the export options to include in the report
 */
export function prepareReportSections(exportOptions: string[]): void {
  exportOptions = sortEnumValues(
    DashboardChartExports,
    exportOptions as DashboardChartExports[],
  )
  const filteredValues = Object.values(DashboardChartExports).filter(
    (value) => !(exportOptions as string[]).includes(value),
  )

  filteredValues.forEach((option) => {
    document.getElementById(_.kebabCase(option))?.classList?.add('no-export')
  })

  const delayPageBreak: boolean = exportOptions.includes(
    DashboardChartExports.FILTERS,
  )

  exportOptions.forEach((option, index) => {
    if (index >= (delayPageBreak ? 2 : 1))
      document.getElementById(_.kebabCase(option)).classList.add('page-break')
  })
}

/**
 * Resets 'page-break' and 'no-export' classes after report has been exported
 *
 * @param exportOptions - An array of strings representing the export options that were included in the report
 */
export function resetReportSections(exportOptions: string[]): void {
  exportOptions = sortEnumValues(
    DashboardChartExports,
    exportOptions as DashboardChartExports[],
  )
  const filteredValues = Object.values(DashboardChartExports).filter(
    (value) => !(exportOptions as string[]).includes(value),
  )

  filteredValues.forEach((option) => {
    document.getElementById(_.kebabCase(option))?.classList?.remove('no-export')
  })

  const elements = document.querySelectorAll('.page-break')
  elements.forEach((element) => element.classList.remove('page-break'))
}
